import { Alert, Card, SkeletonRows } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { parseOrderPrefill } from "../../../../features/fuel/forecast-values.js";
import { FuelOrderForm } from "../../../../features/fuel/order-form.js";
import { useFuelPermissions } from "../../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  useFuelStation,
} from "../../../../features/fuel/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { z } from "zod";

/** Sipariş önerisinden gelen ön dolu satırlar: `<ürünKimliği>:<litre>,…` (bkz. `forecast-values.ts`). */
const searchSchema = z.object({
  ...fuelSearchShape,
  prefill: z.string().max(500).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/siparisler/yeni")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewFuelOrderPage,
});

function NewFuelOrderPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("fuel.orders.new.title")}
      description={t("fuel.orders.new.description")}
      breadcrumb={[
        {
          label: t("menu.fuelOrders"),
          link: (children, className) => (
            <Link to="/akaryakit/siparisler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: t("fuel.orders.new.title") },
      ]}
    >
      {({ companyId }) =>
        permissions.canManageOrders ? (
          <NewFuelOrderBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.orders.new.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function NewFuelOrderBody({ companyId }: { companyId: string }): ReactElement {
  const navigate = useNavigate();
  const station = useFuelStation(companyId);
  const search = Route.useSearch();
  const prefill = parseOrderPrefill(search.prefill).map((line) => ({
    productId: line.productId,
    quantityL: line.quantityL,
  }));

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      {station.showPicker ? (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <StationField selection={station} />
        </Card>
      ) : null}
      <FuelOrderForm
        key={station.stationId}
        companyId={companyId}
        stationId={station.stationId}
        order={null}
        prefill={prefill}
        onSaved={(order) => void navigate({ to: "/akaryakit/siparisler/$id", params: { id: order.id } })}
      />
    </>
  );
}
